import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Crown, Sparkles, Eye, Zap, Heart, Infinity, Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TRIGGERS = {
  visits_limit: {
    icon: Eye,
    title: 'Tu es curieuse aujourd\'hui 👀',
    desc: 'Tu as atteint ta limite de visites quotidienne. Avec Premium, visite **autant de profils que tu veux** !',
    cta: 'Découvrir Premium 👑',
    color: 'from-amber-300 via-amber-400 to-yellow-500',
  },
  contact_limit: {
    icon: Heart,
    title: 'Tu fais des belles connexions 💕',
    desc: 'Tu as utilisé tous tes contacts gratuits du mois. Avec Premium, c\'est **illimité** !',
    cta: 'Continuer mes connexions',
    color: 'from-pink-400 via-rose-400 to-red-400',
  },
  perfect_match_locked: {
    icon: Star,
    title: '🌟 Match Parfait débloqué !',
    desc: 'Une maman a **{score}% d\'affinité** avec toi ! Cette opportunité est rare. Débloque Premium pour lui écrire maintenant 💕',
    cta: 'Écrire à mon Match Parfait',
    color: 'from-purple-400 via-pink-400 to-rose-400',
  },
  photo_popular: {
    icon: Sparkles,
    title: 'Ta photo cartonne 🔥',
    desc: '**{views} mamans** ont vu ta photo récemment ! Avec Premium tu sais **qui** visite ton profil.',
    cta: 'Voir qui me visite',
    color: 'from-orange-400 via-amber-400 to-yellow-400',
  },
  low_visibility: {
    icon: Zap,
    title: 'Booste ta visibilité 🚀',
    desc: 'Cette semaine, ton profil a eu peu de visites. **1 Boost Premium = 10x plus de visibilité** pendant 3h.',
    cta: 'Activer un Boost',
    color: 'from-blue-400 via-indigo-400 to-purple-400',
  },
};

let currentTrigger = null;
let setOpenFn = null;

// API publique pour déclencher l'upsell depuis n'importe où dans l'app
export const triggerUpsell = async (triggerType, context = {}) => {
  if (!TRIGGERS[triggerType]) return;
  const token = localStorage.getItem('mamalink_token') || sessionStorage.getItem('mamalink_token');
  if (!token) return;
  try {
    // Vérifier cap quotidien (1 par jour par type)
    const canShow = await axios.get(`${BACKEND_URL}/api/upsell/can-show/${triggerType}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!canShow.data.can_show) return;
    // Log + ouvrir
    await axios.post(`${BACKEND_URL}/api/upsell/trigger`, { trigger_type: triggerType }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    currentTrigger = { type: triggerType, context };
    if (setOpenFn) setOpenFn(true);
  } catch (e) {
    // silencieux
  }
};

export const ContextualUpsellModal = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpenFn = setOpen;
    return () => { setOpenFn = null; };
  }, []);

  if (!open || !currentTrigger) return null;
  const cfg = TRIGGERS[currentTrigger.type];
  if (!cfg) return null;
  const Icon = cfg.icon;

  const fillTemplate = (text) => {
    return text.replace(/\{(\w+)\}/g, (_, key) => currentTrigger.context[key] ?? '');
  };

  const handleClose = () => setOpen(false);
  const handleCTA = () => {
    setOpen(false);
    navigate('/dashboard/premium');
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
      <div
        className="bg-white rounded-3xl max-w-sm w-full overflow-hidden animate-scale-in"
        data-testid={`upsell-${currentTrigger.type}`}
      >
        <div className={`bg-gradient-to-br ${cfg.color} p-6 text-center relative`}>
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
            data-testid="upsell-close"
          >
            <X className="w-4 h-4 text-white" />
          </button>
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
            <Icon className="w-10 h-10 text-amber-500" />
          </div>
          <h2 className="text-white font-bold text-xl">{fillTemplate(cfg.title)}</h2>
        </div>

        <div className="p-6 text-center">
          <p
            className="text-[#5C3D36] mb-6 text-sm leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: fillTemplate(cfg.desc).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            }}
          />

          <div className="space-y-2 mb-6 bg-amber-50 rounded-2xl p-4 text-left">
            <div className="flex items-center gap-2 text-sm text-[#5C3D36]">
              <Infinity className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <span>Contacts privés illimités</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#5C3D36]">
              <Eye className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <span>Voir qui visite ton profil</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#5C3D36]">
              <Crown className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <span>Badge Premium 👑 + 3 boosts/mois</span>
            </div>
          </div>

          <button
            onClick={handleCTA}
            className="w-full py-4 bg-gradient-to-r from-amber-400 to-yellow-500 text-white rounded-2xl font-bold mb-3 flex items-center justify-center gap-2 hover:shadow-lg transition-shadow"
            data-testid="upsell-cta"
          >
            <Crown className="w-5 h-5" />
            {cfg.cta}
          </button>

          <button
            onClick={handleClose}
            className="w-full py-2 text-[#9D8189] text-sm hover:text-[#5C3D36] transition-colors"
            data-testid="upsell-later"
          >
            Plus tard
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContextualUpsellModal;
